import { useEffect, useState } from 'react';
import { Plus, Package, ClipboardList, Trash2, Loader2, AlertCircle } from 'lucide-react';
import ServiceTemplateManager from './ServiceTemplateManager';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { useVertical } from '../../../context/VerticalContext';
import { supabase } from '../../../lib/supabase';
import { reportError } from '../../../lib/errors';

/**
 * The parts and labour catalogue — the org's own, with the prices it charges.
 *
 * WHAT THIS USED TO BE
 *
 * Two hardcoded rows (`OIL-5W30`, `LAB-STD`) held in useState with a discarded
 * setter, an "ADD ITEM" button with no onClick, and an edit button with no
 * onClick — with a comment admitting as much. It rendered a convincing
 * catalogue that had never been connected to `inventory_catalog` at all, so a
 * user who priced a job off this screen was reading numbers nobody had set.
 *
 * It is now the real table. Prices are edited in place, because a price is the
 * only thing on a catalogue row that changes regularly and a modal for one
 * number is a modal nobody opens.
 *
 * ADDING happens in two places on purpose. Here, deliberately, when someone is
 * setting their prices up; and inline in the parts field on a job, where a
 * mechanic hits a part we do not know mid-write-up. The unique index on the
 * normalised name means neither route can create a second "Brake fluid".
 */

type InventoryType = 'part' | 'labour' | 'service';

interface CatalogItem {
    id: string;
    item_code: string | null;
    name: string;
    type: InventoryType;
    cost_price: number | null;
    retail_price: number | null;
    stock_level: number | null;
}

export default function CatalogManager() {
    const [activeTab, setActiveTab] = useState<'inventory' | 'templates'>('inventory');
    const { settings: orgSettings } = useOrgSettings();
    const { activeOrgId } = useVertical();
    const markup = orgSettings?.default_parts_markup || 0;

    const [items, setItems] = useState<CatalogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState('');
    const [newType, setNewType] = useState<InventoryType>('part');
    const [saving, setSaving] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    // Fetch lives inside the effect, and every setState happens after the await.
    //
    // Calling a state-setting helper from an effect body trips
    // react-hooks/set-state-in-effect — the rule cannot see that the setters sit
    // behind an await, and it is right not to guess. `reloadKey` is the same
    // refetch trigger CustomersPage uses, bumped from event handlers where
    // setting state is perfectly ordinary.
    useEffect(() => {
        if (!activeOrgId) return;
        let cancelled = false;
        void (async () => {
            const { data, error: loadError } = await supabase
                .from('inventory_catalog')
                .select('id, item_code, name, type, cost_price, retail_price, stock_level')
                .eq('org_id', activeOrgId)
                .order('name');
            if (cancelled) return;
            if (loadError) {
                reportError(loadError, 'catalog-manager.inventory_catalog');
                setError('Could not load your catalogue.');
            } else {
                setError(null);
                setItems((data as CatalogItem[] | null) ?? []);
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [activeOrgId, reloadKey]);

    /**
     * Writes the new price straight through on blur.
     *
     * Optimistic, because the alternative on a phone is a spinner on every
     * field. A failed write is put back rather than left showing a number the
     * database does not hold — a silently-wrong price here ends up on invoices.
     */
    const savePrice = async (id: string, field: 'cost_price' | 'retail_price', raw: string) => {
        const value = raw.trim() === '' ? null : Number(raw);
        if (value !== null && !Number.isFinite(value)) return;
        const previous = items.find((i) => i.id === id);
        setItems((p) => p.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
        const { error: saveError } = await supabase
            .from('inventory_catalog').update({ [field]: value }).eq('id', id);
        if (saveError) {
            reportError(saveError, 'catalog-manager.price-update');
            setError('That price did not save.');
            if (previous) setItems((p) => p.map((i) => (i.id === id ? previous : i)));
        }
    };

    const addItem = async () => {
        const name = newName.trim();
        if (!name || !activeOrgId) return;
        setSaving(true);
        const { error: addError } = await supabase
            .from('inventory_catalog')
            .insert({ org_id: activeOrgId, name, type: newType });
        setSaving(false);
        if (addError) {
            // 23505 is the per-org unique index on the normalised name. It is
            // the constraint working, not a fault: say so plainly instead of
            // reporting a database error at the user.
            setError(addError.code === '23505'
                ? `You already have a "${name}" — check the list.`
                : 'Could not add that item.');
            if (addError.code !== '23505') reportError(addError, 'catalog-manager.insert');
            return;
        }
        setNewName('');
        setAdding(false);
        setError(null);
        setReloadKey((k) => k + 1);
    };

    const removeItem = async (id: string, name: string) => {
        if (!window.confirm(`Remove "${name}" from your catalogue?\n\nInvoices and jobs that already used it keep their line and their price.`)) return;
        const { error: delError } = await supabase.from('inventory_catalog').delete().eq('id', id);
        if (delError) {
            reportError(delError, 'catalog-manager.delete');
            setError('Could not remove that item.');
            return;
        }
        setItems((p) => p.filter((i) => i.id !== id));
    };

    const priceInput = 'w-24 bg-tint/5 border border-tint/10 rounded px-2 py-1.5 min-h-[44px] md:min-h-0 text-sm font-mono text-fg text-right outline-none focus:border-accent';

    return (
        <div className="h-full flex flex-col gap-6 p-4 sm:p-6 min-w-0">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-fg tracking-tight">Catalogue &amp; Templates</h1>
                    <p className="text-fg-3 text-sm">Your parts, your prices, and canned jobs</p>
                </div>
                <div className="flex bg-card border border-tint/10 p-1 rounded-lg self-start">
                    <button
                        onClick={() => setActiveTab('inventory')}
                        className={`flex items-center gap-2 px-4 min-h-[44px] rounded-md text-sm font-medium transition-all ${
                            activeTab === 'inventory' ? 'bg-tint/10 text-fg shadow-sm' : 'text-fg-4 hover:text-fg-2'}`}
                    >
                        <Package className="w-4 h-4" /> Inventory
                    </button>
                    <button
                        onClick={() => setActiveTab('templates')}
                        className={`flex items-center gap-2 px-4 min-h-[44px] rounded-md text-sm font-medium transition-all ${
                            activeTab === 'templates' ? 'bg-tint/10 text-fg shadow-sm' : 'text-fg-4 hover:text-fg-2'}`}
                    >
                        <ClipboardList className="w-4 h-4" /> Service Templates
                    </button>
                </div>
            </div>

            {activeTab === 'inventory' ? (
                <>
                    <div className="flex flex-wrap justify-between items-center gap-3">
                        {markup > 0 && (
                            <p className="text-xs text-fg-4 min-w-0">
                                Global parts markup: <span className="text-accent font-bold">{markup}%</span>
                                <span className="text-fg-4 ml-1">(Settings → Pricing &amp; Rates)</span>
                            </p>
                        )}
                        <button
                            onClick={() => setAdding((a) => !a)}
                            className="flex items-center gap-2 fd-btn-primary font-bold px-4 min-h-[44px] rounded-lg transition-all ml-auto"
                        >
                            <Plus className="w-4 h-4" /> Add item
                        </button>
                    </div>

                    {adding && (
                        <div className="flex flex-wrap gap-2 items-center p-3 rounded-lg bg-tint/5 border border-tint/10">
                            <input
                                autoFocus
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addItem(); } }}
                                placeholder="e.g. Brake pads front"
                                className="flex-1 min-w-0 bg-tint/5 border border-tint/10 rounded-lg px-3 min-h-[44px] text-sm text-fg outline-none focus:border-accent"
                            />
                            <select
                                value={newType}
                                onChange={(e) => setNewType(e.target.value as InventoryType)}
                                className="bg-tint/5 border border-tint/10 rounded-lg px-3 min-h-[44px] text-sm text-fg outline-none"
                            >
                                <option value="part">Part</option>
                                <option value="labour">Labour</option>
                                <option value="service">Service</option>
                            </select>
                            <button
                                onClick={() => void addItem()}
                                disabled={saving || !newName.trim()}
                                className="min-h-[44px] px-4 rounded-lg fd-btn-primary text-sm font-bold disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                            </button>
                        </div>
                    )}

                    {error && (
                        <p className="flex items-center gap-2 text-sm text-gold">
                            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                        </p>
                    )}

                    {loading ? (
                        <p className="text-sm text-fg-4 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading your catalogue…
                        </p>
                    ) : items.length === 0 ? (
                        /* An honest empty state. The old screen showed two invented
                           rows here, which read as "you have a catalogue" when the
                           user had nothing at all. */
                        <div className="glass-panel rounded-[var(--fds-radius)] border border-tint/5 p-8 text-center">
                            <Package className="w-8 h-8 mx-auto text-fg-4 mb-3" />
                            <p className="text-sm text-fg-3 font-medium">Your catalogue is empty.</p>
                            <p className="text-xs text-fg-4 mt-2 max-w-md mx-auto">
                                Add the parts you fit most often and the prices you charge for them, and they
                                will come up as you type on a job. You can also save a part straight from a
                                job without coming here.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Cards on a phone, table from md: up — a seven-column
                                table at 320px is unreadable however it scrolls. */}
                            <div className="md:hidden space-y-2">
                                {items.map((item) => (
                                    <div key={item.id} className="rounded-lg bg-tint/5 border border-tint/10 p-3 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-fg break-words">{item.name}</p>
                                                <span className={`inline-block mt-1 text-[10px] uppercase px-2 py-0.5 rounded border ${
                                                    item.type === 'part' ? 'border-info/20 text-info' : 'border-gold/20 text-gold'}`}>
                                                    {item.type}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => void removeItem(item.id, item.name)}
                                                aria-label={`Remove ${item.name}`}
                                                className="shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-fg-4 hover:text-danger"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-3">
                                            <label className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase tracking-wider text-fg-4">Cost</span>
                                                <input type="number" step="0.01" defaultValue={item.cost_price ?? ''}
                                                    onBlur={(e) => void savePrice(item.id, 'cost_price', e.target.value)}
                                                    className={priceInput} />
                                            </label>
                                            <label className="flex flex-col gap-1">
                                                <span className="text-[10px] uppercase tracking-wider text-fg-4">Retail</span>
                                                <input type="number" step="0.01" defaultValue={item.retail_price ?? ''}
                                                    onBlur={(e) => void savePrice(item.id, 'retail_price', e.target.value)}
                                                    className={priceInput} />
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="hidden md:block glass-panel rounded-[var(--fds-radius)] overflow-hidden flex-1 border border-tint/5">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse min-w-[600px]">
                                        <thead>
                                            <tr className="bg-tint/5 border-b border-tint/5 text-xs text-fg-3 uppercase tracking-wider">
                                                <th className="p-4 font-medium">Item name</th>
                                                <th className="p-4 font-medium">Type</th>
                                                <th className="p-4 font-medium text-right">Cost</th>
                                                <th className="p-4 font-medium text-right bg-accent/5">
                                                    Retail{markup > 0 ? ` (+${markup}%)` : ''}
                                                </th>
                                                <th className="p-4 font-medium text-right">Stock</th>
                                                <th className="p-4 font-medium w-10"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-tint/5">
                                            {items.map((item) => (
                                                <tr key={item.id} className="hover:bg-tint/5 transition-colors group">
                                                    <td className="p-4 text-sm text-fg font-medium">{item.name}</td>
                                                    <td className="p-4">
                                                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${
                                                            item.type === 'part' ? 'border-info/20 text-info' : 'border-gold/20 text-gold'}`}>
                                                            {item.type}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 text-right">
                                                        <input type="number" step="0.01" defaultValue={item.cost_price ?? ''}
                                                            onBlur={(e) => void savePrice(item.id, 'cost_price', e.target.value)}
                                                            className={priceInput} />
                                                    </td>
                                                    <td className="p-4 text-right bg-accent/5">
                                                        <input type="number" step="0.01" defaultValue={item.retail_price ?? ''}
                                                            onBlur={(e) => void savePrice(item.id, 'retail_price', e.target.value)}
                                                            className={priceInput} />
                                                    </td>
                                                    <td className="p-4 text-sm text-fg-2 text-right">
                                                        {item.type === 'labour' ? '—' : (item.stock_level ?? '—')}
                                                    </td>
                                                    <td className="p-4">
                                                        {/* fd-hover-reveal, not opacity-0: an
                                                            invisible-but-tappable delete button
                                                            is how one got shipped to phones
                                                            before. This one does something, so
                                                            touch devices simply see it. */}
                                                        <button
                                                            onClick={() => void removeItem(item.id, item.name)}
                                                            aria-label={`Remove ${item.name}`}
                                                            className="fd-hover-reveal p-1.5 hover:bg-tint/10 rounded text-fg-3 hover:text-danger transition-all"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <p className="text-xs text-fg-4">
                                {items.length} item{items.length === 1 ? '' : 's'} — prices save when you tap away.
                            </p>
                        </>
                    )}
                </>
            ) : (
                <ServiceTemplateManager />
            )}
        </div>
    );
}
